import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react'
import { 
  User, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider
} from 'firebase/auth'
import { auth } from '../lib/firebase'

interface AuthContextType {
  user: User | null
  loading: boolean
  isAdmin: boolean
  signIn: (email: string, password: string) => Promise<void>
  signInWithGoogle: () => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)

  const checkAdminClaims = useCallback(async (user: User) => {
    try {
      console.log('Checking admin claims for user:', user.uid)
      
      // Temporal: Considerar al usuario CEO como administrador
      if (user.email === 'ceo@royalsoftware.com.co') {
        console.log('CEO user detected, granting admin access')
        
        // Forzar renovación del token para obtener custom claims actualizados
        try {
          await user.getIdToken(true) // true = force refresh
          const tokenResult = await user.getIdTokenResult(true)
          console.log('Refreshed token claims:', tokenResult.claims)
        } catch (error) {
          console.error('Error refreshing token:', error)
        }
        
        return true
      }
      
      const tokenResult = await user.getIdTokenResult(true) // Force refresh
      console.log('Token claims:', tokenResult.claims)
      const isAdmin = !!tokenResult.claims.admin || !!tokenResult.claims.editor
      console.log('Is admin result:', isAdmin)
      return isAdmin
    } catch (error) {
      console.error('Error checking admin claims:', error)
      // Fallback: Si hay error y es el CEO, dar acceso
      if (user.email === 'ceo@royalsoftware.com.co') {
        return true
      }
      return false
    }
  }, [])

  useEffect(() => {
    let isMounted = true

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!isMounted) return

      console.log('Auth state changed. User:', user?.email, 'UID:', user?.uid)

      try {
        if (user) {
          const adminStatus = await checkAdminClaims(user)
          if (isMounted) {
            console.log('Setting user and admin status:', adminStatus)
            setUser(user)
            setIsAdmin(adminStatus)
          }
        } else {
          if (isMounted) {
            console.log('No user, clearing state')
            setUser(null)
            setIsAdmin(false)
          }
        }
      } catch (error) {
        console.error('Error in auth state change:', error)
        if (isMounted) {
          setUser(null)
          setIsAdmin(false)
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    })

    return () => {
      isMounted = false
      unsubscribe()
    }
  }, [checkAdminClaims])

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password)
    } catch (error) {
      console.error('Sign in error:', error)
      throw error
    }
  }, [])

  const signInWithGoogle = useCallback(async () => {
    try {
      const provider = new GoogleAuthProvider()
      await signInWithPopup(auth, provider)
    } catch (error) {
      console.error('Google sign in error:', error)
      throw error
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await signOut(auth)
    } catch (error) {
      console.error('Logout error:', error)
      throw error
    }
  }, [])

  const value = {
    user,
    loading,
    isAdmin,
    signIn,
    signInWithGoogle,
    logout
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
